import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import _, { set } from "lodash";
import cx from "classnames";

import Button from "src/components/button";

import Slider from "src/components/slider";
import { Course, Question } from "src/types/coaching/courses";

import styles from "./generateQuestions.module.scss";
import { AssetCard } from "src/components/asset/add/card";
import { Asset } from "src/types/assets";
import { generateQuestions, invalidateGenerateQuestions } from "src/redux/actions/coaching/courses";
import { useTransition, animated } from "@react-spring/web";
import Thumbnail from "src/components/thumbnail";
import { FileService } from "src/services/file.service";
import Reveal from "src/components/animation";
import Text from "src/components/text";

const SUPPORTED_FILE_TYPES = ["PDF", "PPTX", "PPT", "DOCX", "DOC", "G-DOC", "G-SHEET"];

interface GenerateQuestionsDialogProps {
  show: boolean;
  course: Course;
  // eslint-disable-next-line no-unused-vars
  onFinish: (questions: Question[]) => void;
  onClose: () => void;
}

export const GenerateQuestionsDialog: React.FC<GenerateQuestionsDialogProps> = ({ show, course, onFinish, onClose }) => {
  const dispatch = useDispatch();

  const [questionCount, setQuestionCount] = useState<number>(3);
  const [selectedLessons, setSelectedLessons] = useState<Asset[]>([]);
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const lessons = useMemo(() => {
    return (course.contents || [])
      .filter((content) => content.entityType === "lesson")
      .filter((lesson) => {
        const metadata = lesson.asset?.metadata;
        if (!metadata) {
          return false;
        }

        const fileType = FileService.getFileType(lesson.asset.metadata);
        if (!_.includes(SUPPORTED_FILE_TYPES, fileType)) {
          return false;
        }
        return true;
      });
  }, [course]);

  const canGenerate = useMemo(() => {
    return _.size(selectedLessons) > 0 && error == null && questionCount > 0;
  }, [selectedLessons, questionCount, error]);

  const generating = useSelector((state) => {
    return _.get(state, `courses.course.${course.id}.generateQuestions.generating`);
  }, _.isEqual);

  const generated = useSelector((state) => {
    return _.get(state, `courses.course.${course.id}.generateQuestions.generated`);
  }, _.isEqual);

  const processedAssets = useSelector((state) => {
    const assets = _.get(state, `courses.course.${course.id}.generateQuestions.assets`) || [];
    return assets.filter((asset: any) => asset.processed);
  }, _.isEqual);

  const questions = useSelector((state) => {
    const assets = _.get(state, `courses.course.${course.id}.generateQuestions.assets`) || [];
    let questions: any[] = [];
    if (assets) {
      assets.forEach((asset: any) => {
        if (_.size(asset.questions) > 0) {
          questions = [...questions, ...asset.questions];
        }
      });
    }

    return questions;
  }, _.isEqual);

  const selectionTransitions = useTransition(!processing, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const processingTransitions = useTransition(processing, {
    initial: { opacity: 0, transform: 'translateY(0)' },
    from: { opacity: 0, transform: 'translateY(100px)' },
    enter: { opacity: 1, transform: 'translateY(0)' },
    leave: { opacity: 0, transform: 'translateY(100px)' },
  });

  const handleLessonSelection = (asset: Asset) => {
    setSelectedLessons((prev) => {
      if (prev.includes(asset)) {
        return prev.filter((item) => item.id !== asset.id);
      } else {
        return [...prev, asset];
      }
    });
  }

  const handleQuestionCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = parseInt(value);

    if (value === "") {
      setQuestionCount(0);
      setError("Questions should be greater than 0");
      return;
    }

    if (numericValue > 50) {
      setError("Questions should be less than 50");
    } else {
      setError(null);
    }

    if (numericValue > 0) {
      setQuestionCount(numericValue);
    }
  }

  const handleGenerateQuestions = () => {
    setProcessing(true);

    const request = {
      course: course.id!,
      questions: questionCount,
      assets: selectedLessons.map((lesson) => lesson.id)
    };

    //Generate questions
    dispatch(generateQuestions(request))
  }

  const handleCancel = () => {
    onClose();
    handleReset();
  }

  const handleReset = () => {
    setSelectedLessons([]);
    setQuestionCount(3);
    setProcessing(false);
    setError(null);
    dispatch(invalidateGenerateQuestions(course.id!));
  }

  useEffect(() => {
    if (generated) {
      onFinish(questions);
      dispatch(invalidateGenerateQuestions(course.id!));
    }
  }, [generated]);

  useEffect(() => {
    const minLessons = Math.max(_.size(selectedLessons), questionCount);
    setQuestionCount(minLessons);
  }, [selectedLessons]);

  useEffect(() => {
    if (show) {
      handleReset();
    }
  }, [show]);

  useEffect(() => {
    if (_.size(processedAssets) > 0) {
      const request = {
        course: course.id!,
        questions: questionCount,
        assets: processedAssets.map((item: any) => item.asset)
      };

      //Generate questions
      dispatch(generateQuestions(request))
    }
  }, [processedAssets]);

  return (
    <Slider.Dialog className={styles.generateQuestionsDialog} width={"400px"} show={show} onClose={onClose}>
      <Slider.Header title="questions from seek" message="generating questions for an assessment with seek" />
      <Slider.Body>
        <div className={styles.body}>
          {selectionTransitions((style, item) => item && (
            <animated.div style={style} className={styles.selectionContainer}>
              <div className={styles.questionField}>
                <label>
                  Choose Number of Questions
                  <Reveal show={error != null}>
                    <div className={styles.error}>{error}</div>
                  </Reveal>
                </label>
                <input type="text"
                  pattern='[0-9]*'
                  value={questionCount}
                  onChange={handleQuestionCountChange}
                />
              </div>

              <div className={styles.lessonsField}>
                <label>Choose lessons to generate questions</label>
                <div className={styles.lessons}>
                  {lessons.map((lesson) => {
                    const selected = selectedLessons.includes(lesson.asset);
                    return (
                      <div key={lesson.uid} className={styles.lesson} onClick={() => handleLessonSelection(lesson.asset)}>
                        <AssetCard editable={false} asset={lesson.asset} />
                        <i className={cx(styles.checkbox, {
                          "fa-solid fa-circle-check": selected,
                          "fa-regular fa-circle": !selected,
                          [styles.selected]: selected,
                        })}></i>
                      </div>
                    );
                  })}
                </div>
              </div>
            </animated.div>
          ))}

          {processingTransitions((style, item) => item && (
            <animated.div style={style} className={styles.progressContainer}>
              <GenerateQuestionsStatus course={course} assets={selectedLessons} />
            </animated.div>
          ))}

        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button variant="primary" icon="paperflite-tick"
          disabled={!canGenerate || generating}
          onClick={handleGenerateQuestions}>
          {generating ? 'Generating...' : 'Generate'}
        </Button>
        <Button onClick={handleCancel} disabled={generating}>Cancel</Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface GenerateQuestionsStatusProps {
  course: Course;
  assets?: Asset[];
}

const GenerateQuestionsStatus: React.FC<GenerateQuestionsStatusProps> = ({ course, assets }) => {
  const statuses = useSelector((state) => {
    const assets = _.get(state, `courses.course.${course.id}.generateQuestions.assets`) || [];
    return assets;
  }, _.isEqual);

  return (
    <div className={styles.assets}>
      {assets?.map((asset, index) => {
        const fileType = FileService.getFileType(asset.metadata);
        const fileSize = FileService.formatSize(asset.metadata?.content_length || 0);

        const status = statuses.find((item: any) => item.asset === asset.id) as any;

        return (
          <div key={index} className={styles.asset}>
            <Thumbnail className={styles.icon} media={asset.icon} defaultImage="/images/assets/default_asset.jpg" />
            <div className={styles.details}>
              <Text className={styles.title} text={asset.name || ""} lines={1} />
              <div className={styles.metadata}>{fileType} | {fileSize}</div>
            </div>
            <div className={styles.status}>
              {status?.status === 'completed' ? <i className={cx("paperflite-tick")} /> : 'pending...'}
            </div>
          </div>
        )
      })}
    </div>
  );
}